import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { UserPlus, Trash2, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

export default function AdminTeamPage() {
  const [members, setMembers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', title: '', linkedin_url: '', bio: '', role: 'contributor', order: 99 });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/team`);
      setMembers(res.data.members || []);
    } catch (e) { console.error(e); }
  };

  const handleAdd = async () => {
    try {
      await axios.post(`${API_BASE}/admin/team`, form);
      toast.success('Team member added');
      setShowAdd(false);
      setForm({ name: '', title: '', linkedin_url: '', bio: '', role: 'contributor', order: 99 });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    await axios.delete(`${API_BASE}/admin/team/${id}`);
    toast.success('Removed');
    load();
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]" data-testid="admin-team">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Team Management</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">{members.length} team members</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="rounded-full h-8 sm:h-9 px-3 sm:px-4 text-xs sm:text-sm w-full sm:w-auto">
          <UserPlus className="w-4 h-4 mr-1.5" /> Add Member
        </Button>
      </div>

      <div className="space-y-2">
        {members.map(m => (
          <Card key={m.id} data-testid={`team-member-${m.name?.replace(/\s/g, '-')}`}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-xs sm:text-sm">{m.name}</span>
                    <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary capitalize">{m.role}</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">{m.title}</p>
                  {m.linkedin_url && (
                    <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-1 mt-1 truncate">
                      <Linkedin className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{m.linkedin_url}</span>
                    </a>
                  )}
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0" onClick={() => handleDelete(m.id)}>
                  <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader><DialogTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-[10px] sm:text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Dr. Jane Smith" className="text-xs h-8" /></div>
            <div className="space-y-1"><Label className="text-[10px] sm:text-xs">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Lead Pharmacometrician" className="text-xs h-8" /></div>
            <div className="space-y-1"><Label className="text-[10px] sm:text-xs">LinkedIn URL</Label><Input value={form.linkedin_url} onChange={e => setForm({...form, linkedin_url: e.target.value})} placeholder="https://linkedin.com/in/..." className="text-xs h-8" /></div>
            <div className="space-y-1"><Label className="text-[10px] sm:text-xs">Bio</Label><Textarea value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} placeholder="Brief bio..." rows={3} className="text-xs" /></div>
            <div className="space-y-1">
              <Label className="text-[10px] sm:text-xs">Role</Label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="founder">Founder</SelectItem>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="contributor">Contributor</SelectItem>
                  <SelectItem value="advisor">Advisor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={handleAdd} className="rounded-full text-xs h-8 w-full sm:w-auto">Add Member</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
